import React, { useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from './ui/button';
import Testimonials from './Testimonials';
import { serviceContent } from '../data/servicePages';

// Service data
const getServiceData = (slug) => {
  const services = {
    'web-development': {
      title: 'Professional Web Development',
      description: 'Build powerful, scalable websites that drive business growth.',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
      overview: 'In the modern digital landscape, your website is your business front door and often the first impression potential customers have of your brand. Invotage creates custom web solutions that combine stunning visual design with powerful functionality and exceptional user experiences. We understand that every business is unique, which is why we take the time to deeply understand your goals, target audience, and competitive landscape before writing a single line of code. Our team of expert developers leverages cutting-edge technologies and proven best practices to build websites that not only look amazing but also perform flawlessly, load instantly, rank well in search engines, and convert visitors into loyal customers. Whether you need a simple landing page, a complex e-commerce platform, or a full-scale enterprise web application, we have the expertise and experience to bring your vision to life and drive measurable business results.',
      benefits: [
        { icon: 'Zap', title: 'Lightning-Fast Performance', desc: 'Optimized code ensures sub-2-second load times.' },
        { icon: 'Shield', title: 'Bank-Grade Security', desc: 'SSL encryption and security audits protect your data.' },
        { icon: 'Search', title: 'SEO-Optimized', desc: 'Built-in SEO best practices for better rankings.' },
        { icon: 'Smartphone', title: 'Mobile-First Design', desc: 'Perfect experiences across all devices.' }
      ],
      process: [
        { step: '01', title: 'Discovery & Planning', desc: 'We analyze your requirements and create a detailed project roadmap.' },
        { step: '02', title: 'Design & Prototyping', desc: 'Create wireframes and interactive prototypes for your approval.' },
        { step: '03', title: 'Development', desc: 'Build your website using cutting-edge technologies and best practices.' },
        { step: '04', title: 'Testing & Launch', desc: 'Rigorous testing followed by smooth deployment and launch.' },
        { step: '05', title: 'Support & Maintenance', desc: 'Ongoing support to keep your website running flawlessly.' }
      ],
      technologies: ['React', 'Next.js', 'Node.js', 'Python', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker'],
      faqs: [
        { q: 'How long does it take to build a website?', a: 'Typically 2-8 weeks depending on complexity and requirements.' },
        { q: 'Do you provide hosting services?', a: 'Yes, we offer secure cloud hosting with 99.9% uptime guarantee.' },
        { q: 'Can you redesign my existing website?', a: 'Absolutely! We specialize in website redesigns and modernization.' }
      ]
    },
    'mobile-app': {
      title: 'Mobile App Development',
      description: 'Transform your business with powerful mobile applications.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c',
      overview: 'Mobile apps have become essential for business success in today\'s mobile-first world, with over 70% of internet users accessing services through smartphones and tablets. Invotage develops intuitive, high-performance mobile applications for iOS and Android that engage users, streamline operations, and open new revenue channels for your business. Our experienced development team uses industry-leading frameworks and methodologies to create apps that are not only visually stunning but also incredibly fast, reliable, and secure. We focus on delivering exceptional user experiences that keep customers coming back, while ensuring your app scales seamlessly as your user base grows. From initial concept and design through development, testing, deployment, and ongoing support, we handle every aspect of the mobile app journey with precision and professionalism.',
      benefits: [
        { icon: 'Smartphone', title: 'Cross-Platform', desc: 'Single codebase for iOS and Android.' },
        { icon: 'Zap', title: 'High Performance', desc: 'Smooth, lag-free user experiences.' },
        { icon: 'Users', title: 'Intuitive UX', desc: 'User-centric design that engages.' },
        { icon: 'Shield', title: 'Secure', desc: 'Industry-standard security protocols.' }
      ],
      process: [
        { step: '01', title: 'Requirement Analysis', desc: 'Understanding your app vision and target audience needs.' },
        { step: '02', title: 'UI/UX Design', desc: 'Creating intuitive interfaces that users love.' },
        { step: '03', title: 'App Development', desc: 'Building native or cross-platform apps with latest frameworks.' },
        { step: '04', title: 'Quality Assurance', desc: 'Extensive testing on real devices for bug-free experience.' },
        { step: '05', title: 'App Store Deployment', desc: 'Publishing to App Store and Google Play with optimization.' }
      ],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'Redux', 'GraphQL', 'Jest'],
      faqs: [
        { q: 'iOS or Android - which should I choose?', a: 'We recommend cross-platform development for both platforms simultaneously.' },
        { q: 'How much does mobile app development cost?', a: 'Starting from $2,999, varies based on features and complexity.' },
        { q: 'Do you provide post-launch support?', a: 'Yes, we offer comprehensive maintenance packages with updates and bug fixes.' }
      ]
    },
    'software-development': {
      title: 'Custom Software Development',
      description: 'Tailored software solutions for unique business challenges.',
      image: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6',
      overview: 'No two businesses operate alike, and off-the-shelf software rarely provides the perfect fit for your unique workflows, processes, and requirements. Invotage specializes in creating custom software solutions that are precisely tailored to address your specific business challenges and opportunities. We create scalable, flexible, and secure systems that streamline workflows, eliminate bottlenecks, improve efficiency, and drive measurable growth. Our development methodology combines agile principles with proven best practices to deliver software that not only meets your current needs but also adapts and evolves as your business grows and market conditions change. From enterprise resource planning systems to customer relationship management platforms, inventory management tools to specialized industry-specific applications, we have the technical expertise and business acumen to build software that becomes a competitive advantage for your organization.',
      benefits: [
        { icon: 'Target', title: 'Tailored Fit', desc: 'Designed specifically for your workflows.' },
        { icon: 'TrendingUp', title: 'Scalable', desc: 'Adapts as your business expands.' },
        { icon: 'Shield', title: 'Secure Code', desc: 'Robust protection against vulnerabilities.' },
        { icon: 'Zap', title: 'Automation', desc: 'Streamline and automate operations.' }
      ],
      process: [
        { step: '01', title: 'Consultation', desc: 'Deep dive into your business challenges and goals.' },
        { step: '02', title: 'Architecture Design', desc: 'Creating scalable system architecture.' },
        { step: '03', title: 'Agile Development', desc: 'Iterative development with regular feedback.' },
        { step: '04', title: 'Integration', desc: 'Seamless integration with existing systems.' },
        { step: '05', title: 'Training', desc: 'Complete training and documentation.' }
      ],
      technologies: ['Java', 'Python', '.NET', 'Spring Boot', 'Django', 'Microservices', 'Docker', 'Kubernetes'],
      faqs: [
        { q: 'What software can you develop?', a: 'CRM, ERP, inventory systems, and custom business applications.' },
        { q: 'How do you ensure quality?', a: 'Through code reviews, automated testing, and QA processes.' },
        { q: 'Can you integrate with existing systems?', a: 'Yes, we specialize in API integrations.' }
      ]
    },
    'ecommerce': {
      title: 'E-Commerce Solutions',
      description: 'Build high-converting online stores that maximize sales.',
      image: 'https://images.pexels.com/photos/89955/pexels-photo-89955.jpeg',
      overview: 'Transform your retail business with cutting-edge e-commerce solutions that convert visitors into loyal customers and maximize every sales opportunity. The modern e-commerce landscape is highly competitive, requiring more than just an online catalog—you need a comprehensive platform that delivers exceptional user experiences, seamless checkout processes, personalized product recommendations, and robust backend management tools. Invotage builds feature-rich online stores using industry-leading platforms like Shopify, WooCommerce, and Magento, customized to match your brand identity and business requirements. Our e-commerce solutions include secure payment processing, inventory management, order fulfillment automation, customer account portals, email marketing integration, and powerful analytics dashboards. Whether you\'re launching your first online store or scaling an existing business to handle thousands of daily transactions, we create e-commerce experiences that drive revenue growth and build lasting customer relationships.',
      benefits: [
        { icon: 'ShoppingCart', title: 'Easy Checkout', desc: 'Streamlined process increases conversions.' },
        { icon: 'CreditCard', title: 'Secure Payments', desc: 'PCI-compliant payment processing.' },
        { icon: 'BarChart', title: 'Analytics', desc: 'Real-time insights optimize strategy.' },
        { icon: 'Globe', title: 'Global Reach', desc: 'Multi-currency and international shipping.' }
      ],
      process: [
        { step: '01', title: 'Store Planning', desc: 'Define your product catalog and business goals.' },
        { step: '02', title: 'Design', desc: 'Create beautiful, conversion-optimized store design.' },
        { step: '03', title: 'Development', desc: 'Build feature-rich e-commerce platform.' },
        { step: '04', title: 'Payment Setup', desc: 'Integrate secure payment gateways.' },
        { step: '05', title: 'Launch & Optimize', desc: 'Go live and continuously improve performance.' }
      ],
      technologies: ['Shopify', 'WooCommerce', 'Magento', 'Stripe', 'PayPal', 'Square', 'Klaviyo', 'Google Analytics'],
      faqs: [
        { q: 'Which platform is best for my store?', a: 'Depends on your needs - Shopify for simplicity, WooCommerce for flexibility, Magento for enterprise.' },
        { q: 'How do you handle payments?', a: 'We integrate secure payment gateways like Stripe, PayPal, and Square with PCI compliance.' },
        { q: 'Can you migrate my existing store?', a: 'Yes, we offer seamless migration services preserving all your data and SEO rankings.' }
      ]
    },
    'blockchain': {
      title: 'Blockchain Development',
      description: 'Harness blockchain for transparent, secure solutions.',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0',
      overview: 'Leverage distributed ledger technology for enhanced transparency, security, and efficiency in your operations across supply chain, finance, healthcare, and beyond. Blockchain represents a paradigm shift in how data is stored, verified, and shared, eliminating intermediaries and creating immutable records that everyone can trust. Invotage specializes in developing custom blockchain solutions, smart contracts, decentralized applications (dApps), and tokenization platforms that solve real business problems. Whether you need to track products through complex supply chains, automate contractual agreements, create transparent voting systems, or build decentralized finance applications, our blockchain experts have the technical knowledge and industry experience to deliver secure, scalable solutions. We work with leading platforms including Ethereum, Hyperledger, Polygon, and Solana, ensuring your blockchain implementation is built on solid foundations with best-in-class security practices and thorough smart contract auditing.',
      benefits: [
        { icon: 'Link', title: 'Smart Contracts', desc: 'Automated, self-executing contracts.' },
        { icon: 'Shield', title: 'Immutable Records', desc: 'Tamper-proof data storage.' },
        { icon: 'Eye', title: 'Transparency', desc: 'Complete visibility into transactions.' },
        { icon: 'Users', title: 'Decentralized', desc: 'Distributed trust across network.' }
      ],
      process: [
        { step: '01', title: 'Use Case Analysis', desc: 'Identify blockchain opportunities in your business.' },
        { step: '02', title: 'Architecture', desc: 'Design blockchain network and consensus mechanism.' },
        { step: '03', title: 'Smart Contract Dev', desc: 'Write and audit secure smart contracts.' },
        { step: '04', title: 'Integration', desc: 'Connect blockchain with existing systems.' },
        { step: '05', title: 'Deployment', desc: 'Launch on mainnet with ongoing support.' }
      ],
      technologies: ['Ethereum', 'Solidity', 'Web3.js', 'Hyperledger', 'Polygon', 'IPFS', 'Truffle', 'Hardhat'],
      faqs: [
        { q: 'Is blockchain right for my business?', a: 'If you need transparency, traceability, or decentralized trust, blockchain can add significant value.' },
        { q: 'How secure are smart contracts?', a: 'We follow best practices and conduct thorough security audits to ensure contract safety.' },
        { q: 'What about scalability?', a: 'We use Layer 2 solutions and sidechains to ensure high throughput and low costs.' }
      ]
    },
    'ui-ux-design': {
      title: 'UI/UX Design',
      description: 'Create beautiful interfaces that users love.',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
      overview: 'We create user experiences that feel natural and interfaces that delight through research-driven design and iterative testing. Great design is not just about aesthetics—it\'s about understanding user behavior, anticipating needs, removing friction, and creating intuitive pathways that guide users toward their goals effortlessly. Invotage\'s design team combines creativity with data-driven insights to craft digital experiences that increase engagement, boost conversions, and build brand loyalty. Our process begins with deep user research including interviews, surveys, and behavioral analysis to understand your target audience. We then create wireframes, prototypes, and high-fidelity designs that balance business objectives with user needs. Every color, button, interaction, and animation is purposefully designed to enhance usability and create emotional connections with your brand. From mobile apps to enterprise software dashboards, we deliver design systems that scale, maintain consistency, and evolve with your product.',
      benefits: [
        { icon: 'Users', title: 'User-Centric', desc: 'Research-driven design approach.' },
        { icon: 'TrendingUp', title: 'Conversions', desc: 'Boost rates by up to 200%.' },
        { icon: 'Heart', title: 'Brand Consistency', desc: 'Cohesive design language.' },
        { icon: 'Zap', title: 'Rapid Prototyping', desc: 'Quick iterations and testing.' }
      ],
      process: [
        { step: '01', title: 'User Research', desc: 'Understand your users needs and pain points.' },
        { step: '02', title: 'Wireframing', desc: 'Create low-fidelity layouts and user flows.' },
        { step: '03', title: 'Visual Design', desc: 'Design high-fidelity mockups with branding.' },
        { step: '04', title: 'Prototyping', desc: 'Build interactive prototypes for testing.' },
        { step: '05', title: 'User Testing', desc: 'Validate designs with real users.' }
      ],
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Principle', 'Framer', 'Miro', 'Hotjar'],
      faqs: [
        { q: 'Do you do user research?', a: 'Yes, we conduct interviews, surveys, and usability testing to understand users.' },
        { q: 'What deliverables do I get?', a: 'Design files, style guide, component library, and developer handoff documentation.' },
        { q: 'Can you redesign my app?', a: 'Absolutely! We specialize in UX audits and redesigns to improve user satisfaction.' }
      ]
    },
    '3d-animation': {
      title: '3D Animation',
      description: 'Award-winning 3D animations that captivate audiences.',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee',
      overview: 'Bring your ideas to life with stunning 3D animations that tell compelling stories and engage audiences across marketing campaigns, product demonstrations, and entertainment projects. In an age where visual content dominates digital spaces, 3D animation offers unparalleled creative possibilities to showcase products, explain complex concepts, and create memorable brand experiences that static imagery simply cannot match. Invotage\'s animation team combines artistic talent with technical expertise to produce cinematic-quality animations that captivate viewers and drive engagement. From architectural visualizations and product renders to character animations and explainer videos, we handle every aspect of the 3D production pipeline including modeling, texturing, rigging, animation, lighting, rendering, and post-production. Our work has been recognized with industry awards and featured across television, digital platforms, and international marketing campaigns.',
      benefits: [
        { icon: 'Film', title: 'Cinematic Quality', desc: 'Professional-grade animations.' },
        { icon: 'Palette', title: 'Creative Storytelling', desc: 'Compelling visual narratives.' },
        { icon: 'Award', title: 'Award-Winning', desc: 'Industry-recognized excellence.' },
        { icon: 'Eye', title: 'Visual Impact', desc: 'Memorable brand experiences.' }
      ],
      process: [
        { step: '01', title: 'Concept Development', desc: 'Brainstorm creative concepts and storyboards.' },
        { step: '02', title: '3D Modeling', desc: 'Create detailed 3D models and assets.' },
        { step: '03', title: 'Animation', desc: 'Bring models to life with smooth animations.' },
        { step: '04', title: 'Rendering', desc: 'High-quality rendering with lighting and effects.' },
        { step: '05', title: 'Post-Production', desc: 'Final touches, color grading, and sound.' }
      ],
      technologies: ['Blender', 'Maya', 'Cinema 4D', 'After Effects', 'Houdini', 'Substance Painter', 'V-Ray', 'Unreal Engine'],
      faqs: [
        { q: 'What can you animate?', a: 'Product demos, explainer videos, character animation, architectural visualization, and more.' },
        { q: 'How long does animation take?', a: 'Typically 2-8 weeks depending on complexity and length.' },
        { q: 'What formats do you deliver?', a: 'MP4, MOV, or any format you need for your platform.' }
      ]
    },
    'cloud-solutions': {
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure for modern businesses.',
      image: 'https://images.unsplash.com/photo-1667984390535-6d03cff0b11a',
      overview: 'Migrate to the cloud with confidence and unlock the full potential of scalable, secure, and cost-effective infrastructure that grows with your business needs. Cloud computing has revolutionized how businesses operate, offering flexibility, reliability, and global reach that traditional on-premise infrastructure simply cannot match. Invotage provides comprehensive cloud solutions including infrastructure migration, application deployment, database management, serverless architectures, and ongoing optimization across AWS, Microsoft Azure, and Google Cloud Platform. Whether you\'re looking to improve performance, reduce operational costs, enhance security, enable remote workforce capabilities, or achieve global scale, our cloud experts design and implement solutions tailored to your specific requirements. We handle everything from initial assessment and migration planning to deployment, monitoring, and 24/7 support, ensuring your transition to the cloud is smooth, secure, and delivers measurable business value.',
      benefits: [
        { icon: 'Cloud', title: '99.9% Uptime', desc: 'Reliable infrastructure always on.' },
        { icon: 'TrendingUp', title: 'Auto-Scaling', desc: 'Automatically handles traffic spikes.' },
        { icon: 'Globe', title: 'Global CDN', desc: 'Fast content delivery worldwide.' },
        { icon: 'Shield', title: 'Secure', desc: 'Enterprise-grade security.' }
      ],
      process: [
        { step: '01', title: 'Assessment', desc: 'Evaluate current infrastructure and requirements.' },
        { step: '02', title: 'Architecture', desc: 'Design optimal cloud architecture.' },
        { step: '03', title: 'Migration', desc: 'Seamless migration with zero downtime.' },
        { step: '04', title: 'Optimization', desc: 'Performance tuning and cost optimization.' },
        { step: '05', title: 'Monitoring', desc: '24/7 monitoring and support.' }
      ],
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Terraform', 'CloudFlare', 'Nginx'],
      faqs: [
        { q: 'Which cloud provider should I choose?', a: 'We recommend based on your specific needs - AWS for breadth, Azure for Microsoft stack, GCP for AI/ML.' },
        { q: 'How much does cloud hosting cost?', a: 'Starts from $50/month, varies by usage. We optimize costs throughout.' },
        { q: 'Is my data safe in the cloud?', a: 'Yes, with encryption, backups, and enterprise-grade security measures.' }
      ]
    },
    'marketing': {
      title: 'Digital Marketing',
      description: 'Data-driven marketing that delivers ROI.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
      overview: 'Drive sustainable growth with strategic digital marketing campaigns that reach your target audience, generate qualified leads, and convert prospects into loyal customers through proven, data-driven methodologies. The digital marketing landscape is constantly evolving with new platforms, algorithms, and consumer behaviors, making it challenging for businesses to stay ahead and maximize their marketing ROI. Invotage offers comprehensive digital marketing services including search engine optimization (SEO), pay-per-click advertising (PPC), social media marketing, email campaigns, content marketing, and conversion rate optimization. Our marketing experts combine creative storytelling with analytical rigor to develop campaigns that resonate with your audience and deliver measurable results. We don\'t just drive traffic—we attract the right visitors, engage them with compelling content, nurture relationships through strategic touchpoints, and optimize every step of the customer journey to maximize conversions and lifetime value.',
      benefits: [
        { icon: 'Target', title: 'Lead Generation', desc: 'Attract qualified leads consistently.' },
        { icon: 'TrendingUp', title: 'Brand Awareness', desc: 'Increase visibility and recognition.' },
        { icon: 'BarChart', title: 'Data-Driven', desc: 'Measurable results and ROI.' },
        { icon: 'Users', title: 'Audience Targeting', desc: 'Reach the right people.' }
      ],
      process: [
        { step: '01', title: 'Strategy', desc: 'Develop comprehensive marketing strategy.' },
        { step: '02', title: 'Content Creation', desc: 'Create engaging content and creatives.' },
        { step: '03', title: 'Campaign Launch', desc: 'Execute across multiple channels.' },
        { step: '04', title: 'Optimization', desc: 'Continuous A/B testing and improvements.' },
        { step: '05', title: 'Reporting', desc: 'Detailed analytics and ROI tracking.' }
      ],
      technologies: ['Google Ads', 'Facebook Ads', 'LinkedIn', 'HubSpot', 'Mailchimp', 'Google Analytics', 'SEMrush', 'Hootsuite'],
      faqs: [
        { q: 'What marketing channels do you use?', a: 'SEO, PPC, social media, email marketing, and content marketing.' },
        { q: 'How do you measure success?', a: 'Through KPIs like leads, conversions, ROI, and customer acquisition cost.' },
        { q: 'Do you handle social media?', a: 'Yes, we create content, manage accounts, and run paid campaigns.' }
      ]
    },
    'copywriting': {
      title: 'Professional Copywriting',
      description: 'Conversion-focused copy that drives action.',
      image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a',
      overview: 'Words matter, and the right message delivered at the right moment can transform browsers into buyers, subscribers into customers, and customers into brand advocates. Invotage\'s expert copywriters create compelling content that resonates with your target audience, communicates your unique value proposition, and drives measurable business results across every touchpoint of the customer journey. In today\'s crowded digital landscape, generic, forgettable content gets ignored—you need strategic copy that captures attention, builds trust, addresses pain points, and motivates action. Whether you need website copy that converts visitors, blog posts that establish thought leadership, email sequences that nurture leads, advertising copy that stops the scroll, or product descriptions that sell, our writers combine persuasive techniques with SEO best practices to create content that performs. Every word is crafted with intention, backed by research, and optimized for both human readers and search engines to maximize visibility and impact.',
      benefits: [
        { icon: 'FileText', title: 'SEO Optimized', desc: 'Content that ranks and converts.' },
        { icon: 'Target', title: 'Conversion Copy', desc: 'Persuasive writing that sells.' },
        { icon: 'MessageCircle', title: 'Brand Voice', desc: 'Consistent messaging across channels.' },
        { icon: 'TrendingUp', title: 'Engagement', desc: 'Content that resonates.' }
      ],
      process: [
        { step: '01', title: 'Brief', desc: 'Understand your brand, audience, and goals.' },
        { step: '02', title: 'Research', desc: 'Competitor analysis and keyword research.' },
        { step: '03', title: 'Writing', desc: 'Craft compelling, on-brand copy.' },
        { step: '04', title: 'Review', desc: 'Revisions based on your feedback.' },
        { step: '05', title: 'Delivery', desc: 'Final copy ready for publication.' }
      ],
      technologies: ['Grammarly', 'Hemingway', 'Ahrefs', 'Surfer SEO', 'Clearscope', 'Google Docs', 'Notion', 'ChatGPT'],
      faqs: [
        { q: 'What types of copy do you write?', a: 'Website copy, blog posts, emails, ads, product descriptions, and more.' },
        { q: 'How long does it take?', a: 'Website copy: 1-2 weeks. Blog posts: 3-5 days. Ads: 1-2 days.' },
        { q: 'Do you do revisions?', a: 'Yes, we include 2-3 rounds of revisions to ensure satisfaction.' }
      ]
    }
  };
  
  return services[slug] || services['web-development'];
};

const ServicePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.replace('/', ''); // Extract slug from path
  const service = getServiceData(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleGetStarted = (plan) => {
    navigate('/checkout', { state: { package: plan } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-5xl sm:text-6xl font-bold text-white leading-tight" style={{ letterSpacing: '0.05em' }}>
                {service.title}
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                {service.description}
              </p>
              <div className="pt-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-purple-500/50 group">
                    Get Started Today
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={service.image}
                alt={service.title}
                className="rounded-3xl shadow-2xl shadow-purple-900/50 border border-purple-500/20 w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">What We Offer</h2>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
            {service.overview}
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Key Benefits</h2>
            <p className="text-gray-400">Why choose our {service.title.toLowerCase()} services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.benefits.map((benefit, index) => {
              const Icon = LucideIcons[benefit.icon];
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-900/30"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-purple-500/50">
                    {Icon && <Icon className="h-7 w-7 text-white" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      {service.process && (
        <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Our Process</h2>
              <p className="text-gray-400">How we deliver exceptional results</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {service.process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 h-full">
                    <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                  {index < service.process.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Technologies Section */}
      {service.technologies && (
        <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Technologies We Use</h2>
              <p className="text-gray-400">Cutting-edge tools and frameworks</p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {service.technologies.map((tech, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 border border-purple-500/20 rounded-xl px-6 py-3 hover:border-purple-500/50 hover:scale-105 transition-all duration-300"
                >
                  <span className="text-white font-semibold">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/50">
                <div className="w-4 h-4 bg-white rounded-sm"></div>
              </div>
              <span className="text-purple-400 font-semibold uppercase tracking-wider text-sm">
                OUR PRICING
              </span>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4"
              style={{ letterSpacing: '0.1em' }}
            >
              Plans Built For Growth
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceContent[slug]?.pricing?.map((plan, index) => {
              // Map pricing icons based on package type
              const iconMapping = {
                0: 'Zap',        // First package - Starter/Basic
                1: 'Rocket',     // Second package - Growth/Professional (featured)
                2: 'Award'       // Third package - Enterprise
              };
              const Icon = LucideIcons[iconMapping[index]] || LucideIcons.Package;
              
              return (
                <div
                  key={index}
                  className={`relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-xl border ${
                    plan.featured ? 'ring-2 ring-purple-500 transform scale-105 border-purple-500/50' : 'border-purple-500/20'
                  } hover:shadow-2xl hover:shadow-purple-900/50 transition-all duration-300 flex flex-col`}
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.2}s both`
                  }}
                >
                  {plan.featured && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1 text-sm font-semibold shadow-lg z-10">
                      POPULAR
                    </div>
                  )}

                  {/* Fixed Header Section */}
                  <div className="p-8 pb-4 flex-shrink-0">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/50">
                      {Icon && <Icon className="h-8 w-8 text-white" />}
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline">
                        <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">${plan.price}</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mt-2">{plan.name}</h3>
                    </div>

                    {/* Description */}
                    <p className="text-gray-400 mb-6">{plan.description}</p>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handleGetStarted({
                        id: `${slug}-${index}`,
                        name: plan.name,
                        price: `$${plan.price}`,
                        description: plan.description,
                        features: plan.features,
                        icon: iconMapping[index]
                      })}
                      className={`w-full py-6 rounded-full font-semibold ${
                        plan.featured
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/50'
                          : 'bg-gray-700 hover:bg-gray-600 text-white'
                      }`}
                    >
                      Get Started
                    </Button>
                  </div>

                  {/* Scrollable Details Section */}
                  <div className="px-8 pb-8 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-gray-800 hover:scrollbar-thumb-purple-500" style={{ maxHeight: '350px' }}>
                    {/* Features List */}
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-400">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Decorative element */}
                  <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-600/20 rounded-full opacity-50 blur-2xl"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQs Section */}
      {service.faqs && (
        <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-4">
              {service.faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-purple-500/20"
                >
                  <h3 className="text-lg font-bold text-white mb-2">{faq.q}</h3>
                  <p className="text-gray-400">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Lets discuss your project and create something amazing together.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-purple-500/50 group">
              Contact Us Now
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
